// Decides whether the chat composer should grab keyboard focus.
//
// Why this exists: `?` and `/` are global single-key shortcuts (see
// `useCommandPaletteHotkeys`) that only defer to the composer when focus
// is literally inside its textarea. Whenever focus drifts off it — body
// on a fresh load, the `+` button after a click — the next keystroke is
// hijacked into the cheat sheet / palette instead of being typed. Keeping
// the composer focused is the fix; this is the pure guard that gates it.

export interface AutoFocusContext {
  // A coarse pointer (touch). Force-focusing here would pop the on-screen
  // keyboard, so we never auto-focus on touch.
  pointerCoarse: boolean;
  // A modal (⌘K palette / `?` cheat sheet) is open — don't steal its focus.
  overlayOpen: boolean;
  // tagName of document.activeElement, or null when nothing is focused.
  activeElementTag: string | null;
  // Whether the active element is contentEditable.
  activeElementEditable: boolean;
}

const TYPING_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

export function shouldAutoFocusComposer(ctx: AutoFocusContext): boolean {
  if (ctx.pointerCoarse) return false;
  if (ctx.overlayOpen) return false;
  if (ctx.activeElementEditable) return false;
  // Already in a typing surface — either our own textarea (no-op, so this
  // stays idempotent) or another input we must not steal from.
  if (ctx.activeElementTag && TYPING_TAGS.has(ctx.activeElementTag)) {
    return false;
  }
  return true;
}
